import { SEOHead } from "@/components/SEOHead";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Cpu,
  TrendingUp,
  Rocket,
  Briefcase,
  Globe,
  MapPin,
  Handshake,
  Leaf,
  Plane,
  GraduationCap,
} from "lucide-react";

export type Service = {
  id: string;
  icon: typeof Cpu;
  title: string;
  desc: string;
  fullDesc: string;
  features: string[];
};

export const getServices = (isRu: boolean): Service[] => [
  {
    id: "ai-digital",
    icon: Cpu,
    title: isRu ? "ИИ и Цифровая трансформация" : "AI & Digital Transformation",
    desc: isRu
      ? "Внедрение передовых ИИ-решений и цифровых технологий для ускорения роста вашего бизнеса."
      : "Leverage cutting-edge AI solutions and digital technologies to accelerate your business growth.",
    fullDesc: isRu
      ? "Мы помогаем компаниям внедрять искусственный интеллект и цифровые инструменты для оптимизации процессов, автоматизации рутины и принятия данных решений. От машинного обучения до автоматизации документооборота — мы создаём решения, которые работают на ваш бизнес."
      : "We help companies adopt artificial intelligence and digital tools to optimize processes, automate routine tasks, and make data-driven decisions. From machine learning to document automation, we build solutions that work for your business.",
    features: isRu
      ? [
          "Автоматизация бизнес-процессов с помощью ИИ",
          "Внедрение машинного обучения и предиктивной аналитики",
          "Цифровизация документооборота и рабочих процессов",
          "Интеграция ИИ-инструментов в существующие системы",
          "Обучение команд работе с новыми технологиями",
        ]
      : [
          "AI-powered business process automation",
          "Machine learning and predictive analytics implementation",
          "Digitization of document workflows and operations",
          "Integration of AI tools into existing systems",
          "Team training on emerging technologies",
        ],
  },
  {
    id: "investments",
    icon: TrendingUp,
    title: isRu ? "Инвестиции для стартапов" : "Investments for Startups",
    desc: isRu
      ? "Стратегическое финансирование и инвестиционная поддержка для быстрорастущих стартапов."
      : "Strategic funding and investment support for fast-growing startups.",
    fullDesc: isRu
      ? "AITIM Group предоставляет стартапам доступ к стратегическому капиталу, менторской поддержке и сети инвесторов. Мы помогаем на каждом этапе — от seed-раунда до масштабирования, обеспечивая не только финансирование, но и экспертизу для устойчивого роста."
      : "AITIM Group provides startups with access to strategic capital, mentorship, and investor networks. We support you at every stage — from seed rounds to scaling — offering not just funding but the expertise for sustainable growth.",
    features: isRu
      ? [
          "Доступ к сети ангелов и венчурных фондов",
          "Подготовка к инвестиционным раундам (pitch deck, due diligence)",
          "Менторская поддержка от опытных предпринимателей",
          "Стратегическое планирование роста и масштабирования",
          "Сопровождение сделок и переговоров с инвесторами",
        ]
      : [
          "Access to angel investors and venture capital networks",
          "Investment round preparation (pitch deck, due diligence)",
          "Mentorship from experienced entrepreneurs",
          "Strategic growth and scaling planning",
          "Deal facilitation and investor negotiations",
        ],
  },
  {
    id: "startups",
    icon: Rocket,
    title: isRu ? "Стартапы и Венчурное строительство" : "Startups & Venture Building",
    desc: isRu
      ? "От идеи до масштабируемого бизнеса — полное сопровождение на каждом этапе создания стартапа."
      : "From idea to scalable business — full support at every stage of venture building.",
    fullDesc: isRu
      ? "Мы превращаем идеи в жизнеспособные бизнесы. Наш подход охватывает валидацию концепции, разработку MVP, поиск продукт-рыночного соответствия, построение команды и стратегию выхода. Мы работаем как партнёр, разделяя риски и успех."
      : "We turn ideas into viable businesses. Our approach covers concept validation, MVP development, finding product-market fit, team building, and exit strategy. We work as a partner, sharing both risks and success.",
    features: isRu
      ? [
          "Валидация идеи и исследование рынка",
          "Разработка MVP и тестирование гипотез",
          "Поиск продукт-рыночного соответствия (Product-Market Fit)",
          "Построение команды и корпоративной культуры",
          "Стратегия масштабирования и выхода на новые рынки",
        ]
      : [
          "Idea validation and market research",
          "MVP development and hypothesis testing",
          "Product-Market Fit discovery",
          "Team building and corporate culture",
          "Scaling strategy and market expansion",
        ],
  },
  {
    id: "business-consulting",
    icon: Briefcase,
    title: isRu ? "Бизнес-консалтинг и Стратегия" : "Business Consulting & Strategy",
    desc: isRu
      ? "Экспертные стратегические решения для устойчивого роста и конкурентного преимущества."
      : "Expert strategic solutions for sustainable growth and competitive advantage.",
    fullDesc: isRu
      ? "Наши консультанты помогают компаниям разрабатывать и реализовывать стратегии роста, оптимизировать операционную эффективность и находить новые источники дохода. Мы сочетаем глубокий анализ данных с практическим опытом для достижения измеримых результатов."
      : "Our consultants help companies develop and execute growth strategies, optimize operational efficiency, and find new revenue streams. We combine deep data analysis with practical experience to achieve measurable results.",
    features: isRu
      ? [
          "Стратегическое планирование и бизнес-моделирование",
          "Операционный аудит и оптимизация процессов",
          "Финансовое моделирование и анализ доходности",
          "Разработка стратегии выхода на новые рынки",
          "Управление изменениями и трансформация бизнеса",
        ]
      : [
          "Strategic planning and business modeling",
          "Operational audit and process optimization",
          "Financial modeling and profitability analysis",
          "Market entry strategy development",
          "Change management and business transformation",
        ],
  },
  {
    id: "market-entry",
    icon: Globe,
    title: isRu ? "Выход на рынок и Soft Landing" : "Market Entry & Soft Landing",
    desc: isRu
      ? "Бесшовный выход на новые рынки с полной инфраструктурной и правовой поддержкой."
      : "Seamless expansion into new markets with full infrastructure and legal support.",
    fullDesc: isRu
      ? "Мы обеспечиваем компаниям плавный выход на новые рынки, предоставляя юридическую, налоговую и операционную поддержку. От регистрации компании до найма локальной команды — мы создаём условия для быстрого и безопасного запуска."
      : "We ensure companies a smooth entry into new markets, providing legal, tax, and operational support. From company registration to hiring a local team, we create the conditions for a fast and safe launch.",
    features: isRu
      ? [
          "Регистрация компании и юридическая поддержка",
          "Налоговое планирование и оптимизация",
          "Поиск офиса и настройка инфраструктуры",
          "Найм локальной команды и HR-поддержка",
          "Соблюдение местных регуляций и стандартов",
        ]
      : [
          "Company registration and legal support",
          "Tax planning and optimization",
          "Office search and infrastructure setup",
          "Local team hiring and HR support",
          "Compliance with local regulations and standards",
        ],
  },
  {
    id: "central-asia-usa",
    icon: MapPin,
    title: isRu ? "Центральная Азия ↔ США" : "Central Asia ↔ USA",
    desc: isRu
      ? "Мост между рынками Центральной Азии и США — инвестиции, торговля и партнерства."
      : "A bridge between Central Asian and US markets — investments, trade, and partnerships.",
    fullDesc: isRu
      ? "AITIM Group соединяет рынки Центральной Азии и США, создавая возможности для инвестиций, торговли и партнёрств. Мы помогаем компаниям из обоих регионов находить точки роста, строить транснациональные связи и развивать международный бизнес."
      : "AITIM Group connects the markets of Central Asia and the USA, creating opportunities for investment, trade, and partnerships. We help companies from both regions find growth points, build transnational ties, and develop international business.",
    features: isRu
      ? [
          "Поиск инвестиционных возможностей в обоих регионах",
          "Содействие торговле и экспорту/импорту",
          "Построение транснациональных партнёрств",
          "Кросс-культурный бизнес-консалтинг",
          "Сопровождение международных сделок",
        ]
      : [
          "Investment opportunity sourcing in both regions",
          "Trade and export/import facilitation",
          "Building transnational partnerships",
          "Cross-cultural business consulting",
          "International deal facilitation",
        ],
  },
  {
    id: "partnerships",
    icon: Handshake,
    title: isRu ? "Стратегические партнерства" : "Strategic Partnerships",
    desc: isRu
      ? "Построение долгосрочных альянсов с ключевыми игроками для взаимного роста."
      : "Building long-term alliances with key industry players for mutual growth.",
    fullDesc: isRu
      ? "Мы помогаем компаниям находить и выстраивать стратегические партнёрства с ключевыми игроками индустрии. Наш подход включает идентификацию потенциальных партнёров, переговоры, структурирование сделок и управление отношениями для долгосрочного взаимного роста."
      : "We help companies find and build strategic partnerships with key industry players. Our approach includes identifying potential partners, negotiations, deal structuring, and relationship management for long-term mutual growth.",
    features: isRu
      ? [
          "Идентификация и оценка потенциальных партнёров",
          "Ведение переговоров и структурирование сделок",
          "Управление партнёрскими отношениями",
          "Создание совместных предприятий (Joint Ventures)",
          "Стратегические альянсы и кооперации",
        ]
      : [
          "Identification and evaluation of potential partners",
          "Negotiation and deal structuring",
          "Partnership relationship management",
          "Joint venture creation",
          "Strategic alliances and cooperations",
        ],
  },
  {
    id: "eco-projects",
    icon: Leaf,
    title: isRu ? "Эко-проекты" : "Eco Projects",
    desc: isRu
      ? "Устойчивые экологические инициативы и зелёные технологии для будущего бизнеса."
      : "Sustainable environmental initiatives and green technologies for the future of business.",
    fullDesc: isRu
      ? "Мы поддерживаем и развиваем экологические проекты, направленные на устойчивое развитие и внедрение зелёных технологий. От возобновляемых источников энергии до управления отходами — мы помогаем бизнесу быть экологически ответственным и экономически эффективным."
      : "We support and develop environmental projects focused on sustainable development and green technology adoption. From renewable energy to waste management, we help businesses be environmentally responsible and economically efficient.",
    features: isRu
      ? [
          "Проекты возобновляемой энергетики",
          "Внедрение зелёных технологий в производство",
          "Управление отходами и переработка",
          "ESG-консалтинг и устойчивое развитие",
          "Эко-сертификация и соответствие стандартам",
        ]
      : [
          "Renewable energy projects",
          "Green technology implementation in production",
          "Waste management and recycling",
          "ESG consulting and sustainable development",
          "Eco-certification and standards compliance",
        ],
  },
  {
    id: "tourism",
    icon: Plane,
    title: isRu ? "Туризм и Гостеприимство" : "Tourism & Hospitality",
    desc: isRu
      ? "Развитие туристических направлений и повышение стандартов гостеприимства."
      : "Developing tourism destinations and elevating hospitality standards.",
    fullDesc: isRu
      ? "Мы развиваем туристические направления и повышаем стандарты гостеприимства, создавая уникальный опыт для путешественников. Наш подход включает разработку туристических продуктов, маркетинг направлений и обучение персонала для мирового уровня сервиса."
      : "We develop tourism destinations and elevate hospitality standards, creating unique experiences for travelers. Our approach includes tourism product development, destination marketing, and staff training for world-class service.",
    features: isRu
      ? [
          "Разработка туристических продуктов и маршрутов",
          "Маркетинг и продвижение направлений",
          "Обучение персонала и стандарты сервиса",
          "Управление отелями и гостиничными объектами",
          "Цифровая трансформация в туризме",
        ]
      : [
          "Tourism product and route development",
          "Destination marketing and promotion",
          "Staff training and service standards",
          "Hotel and hospitality property management",
          "Digital transformation in tourism",
        ],
  },
  {
    id: "media-education",
    icon: GraduationCap,
    title: isRu ? "Медиа и Бизнес-образование" : "Media & Business Education",
    desc: isRu
      ? "Образовательные программы и медиа-контент для развития предпринимательских навыков."
      : "Educational programs and media content to develop entrepreneurial skills.",
    fullDesc: isRu
      ? "Мы создаём образовательные программы и медиа-контент, которые помогают предпринимателям развивать навыки, необходимые для успеха в современном бизнесе. От курсов по предпринимательству до медиа-проектов, которые вдохновляют и обучают."
      : "We create educational programs and media content that help entrepreneurs develop the skills needed for success in modern business. From entrepreneurship courses to media projects that inspire and educate.",
    features: isRu
      ? [
          "Программы по предпринимательству и лидерству",
          "Производство образовательного медиа-контента",
          "Бизнес-курсы и мастер-классы от экспертов",
          "Менторские программы и нетворкинг",
          "Создание обучающих платформ",
        ]
      : [
          "Entrepreneurship and leadership programs",
          "Educational media content production",
          "Business courses and expert masterclasses",
          "Mentorship programs and networking",
          "Learning platform development",
        ],
  },
];

const businessSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  serviceType: "Business Services & Consulting",
  provider: { "@type": "Organization", name: "AITIM Group" },
  areaServed: ["US", "Central Asia"],
  description:
    "Comprehensive business services including AI & Digital Transformation, startup investments, venture building, business consulting, market entry, strategic partnerships, eco projects, tourism, and media education.",
};

const BusinessServices = () => {
  const { language } = useLanguage();
  const isRu = language === "ru";
  const services = getServices(isRu);


  return (
    <div className="min-h-screen bg-background font-sans">
      <SEOHead
        title={
          isRu
            ? "Бизнес-услуги | AITIM Group"
            : "Business Services | AITIM Group"
        }
        description={
          isRu
            ? "ИИ, инвестиции, стартапы, бизнес-консалтинг, выход на рынок, эко-проекты, туризм и медиа-образование — всё в одном месте."
            : "AI, investments, startups, business consulting, market entry, eco projects, tourism, and media education — all in one place."
        }
        canonical="/business-services"
        schemaJson={businessSchema}
      />
      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative pt-28 pb-16 lg:pt-48 lg:pb-32 overflow-hidden bg-navy-950">
          <div className="absolute inset-0 z-0">
            <video
              autoPlay
              loop
              muted
              playsInline
              className="w-full h-full object-cover object-center hidden md:block"
            >
              <source
                src="https://assets.cdn.filesafe.space/gUCbLLrJwD5vtwXYoPEZ/media/69f127e8c428703a042e614e.webm"
                type="video/webm"
              />
            </video>
            <img
              src="https://assets.cdn.filesafe.space/gUCbLLrJwD5vtwXYoPEZ/media/69f0ff9b40534248c5a223af.webp"
              srcSet="https://assets.cdn.filesafe.space/gUCbLLrJwD5vtwXYoPEZ/media/69f0ff9b40534248c5a223af.webp?w=480 480w, https://assets.cdn.filesafe.space/gUCbLLrJwD5vtwXYoPEZ/media/69f0ff9b40534248c5a223af.webp?w=768 768w, https://assets.cdn.filesafe.space/gUCbLLrJwD5vtwXYoPEZ/media/69f0ff9b40534248c5a223af.webp?w=1200 1200w"
              sizes="(max-width: 768px) 100vw, 1200px"
              alt="Business Services"
              fetchPriority="high"
              className="w-full h-full object-cover object-center md:hidden"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-navy-950/95 via-navy-950/80 to-navy-950/50" />
          </div>

          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-3xl">
              <div className="inline-block px-4 py-1.5 rounded-full bg-primary/20 text-primary font-semibold text-sm mb-6 border border-primary/30 tracking-wide uppercase">
                {isRu ? "Бизнес-услуги" : "Business Services"}
              </div>
              <h1 className="text-3xl md:text-5xl lg:text-7xl font-bold text-white mb-6 leading-tight tracking-tight">
                {isRu ? "Развиваем бизнес" : "Empowering"} <br />
                <span className="text-primary">
                  {isRu ? "по всему миру" : "Across Borders"}
                </span>
              </h1>
              <p className="text-lg lg:text-xl text-white/80 mb-10 max-w-2xl leading-relaxed">
                {isRu
                  ? "От ИИ-трансформации и инвестиций до выхода на рынок и эко-проектов — AITIM Group предоставляет комплексные решения для роста вашего бизнеса."
                  : "From AI transformation and investments to market entry and eco projects — AITIM Group provides comprehensive solutions for your business growth."}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/free-consultation">
                  <Button
                    size="lg"
                    className="bg-primary hover:bg-primary/90 text-white rounded-sm px-8 h-14 text-lg w-full sm:w-auto"
                  >
                    {isRu ? "Связаться с нами" : "Get In Touch"}{" "}
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/free-consultation" className="w-full sm:w-auto">
                  <Button
                    size="lg"
                    variant="outline"
                    className="border-white/20 bg-white/5 text-white hover:bg-white/10 hover:text-white rounded-sm px-8 h-14 text-lg w-full"
                  >
                    {isRu ? "Наши услуги" : "Our Services"}{" "}
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Services Grid */}
        <section className="py-24 bg-slate-50 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent" />
          <div className="container mx-auto px-4 relative z-10">
            <div className="text-center mb-16">
              <div className="text-primary font-bold tracking-widest uppercase text-xs mb-4">
                {isRu ? "Наш опыт" : "Our Expertise"}
              </div>
              <h2 className="text-3xl md:text-5xl font-extrabold text-navy-950">
                {isRu ? "Комплексные" : "Comprehensive"}{" "}
                <span className="text-primary">
                  {isRu ? "услуги" : "Services"}
                </span>
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {services.map((service) => {
                const Icon = service.icon;
                return (
                  <div
                    key={service.id}
                    id={service.id}
                    className="group bg-white rounded-3xl border border-slate-100 shadow-lg hover:shadow-xl hover:-translate-y-2 transition-all duration-300 overflow-hidden scroll-mt-28"
                  >
                    <div className="p-8">
                      <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-6 transition-transform duration-300">
                        <Icon className="w-7 h-7 text-primary" strokeWidth={1.8} />
                      </div>
<Link to={`/business-services/${service.id}`} className="block">
                        <h3 className="text-xl font-bold text-navy-950 mb-3 leading-tight hover:text-primary transition-colors">
                          {service.title}
                        </h3>
                      </Link>
                      <p className="text-slate-500 leading-relaxed text-sm mb-6">
                        {service.desc}
                      </p>
                      <Link to={`/business-services/${service.id}`}>
                        <Button
                          variant="outline"
                          className="border-slate-200 text-navy-950 hover:bg-primary hover:text-white hover:border-primary rounded-xl px-5 h-10 font-semibold bg-white text-sm w-full sm:w-auto"
                        >
                          {isRu ? "Узнать больше" : "Learn More"}{" "}
                          <ArrowRight className="ml-2 w-4 h-4" />
                        </Button>
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 bg-navy-950 text-white relative overflow-hidden">
          <div className="absolute inset-0 bg-[url('https://assets.cdn.filesafe.space/gUCbLLrJwD5vtwXYoPEZ/media/69f0ff9b40534248c5a223af.webp')] opacity-10 bg-cover bg-center" />
          <div className="absolute inset-0 bg-gradient-to-b from-navy-950 via-navy-950/90 to-navy-950" />
          <div className="container mx-auto px-4 text-center relative z-10">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-8">
              {isRu ? "Готовы начать?" : "Ready to get started?"}
            </h2>
            <p className="text-lg text-white/70 mb-10 max-w-2xl mx-auto">
              {isRu
                ? "Свяжитесь с нами для бесплатной консультации и узнайте, как мы можем помочь вашему бизнесу расти."
                : "Get in touch for a free consultation and discover how we can help your business grow."}
            </p>
            <Link to="/free-consultation">
              <Button
                size="lg"
                className="bg-primary hover:bg-primary/90 text-white rounded-full px-12 h-16 text-xl font-bold shadow-2xl transform hover:-translate-y-1 transition-all duration-300"
              >
                {isRu ? "Бесплатная консультация" : "Free Consultation"}{" "}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default BusinessServices;
